import { useCallback, useMemo, useEffect, Dispatch, SetStateAction } from "react"
import { isEmpty, size } from "lodash"
import Filters from "components/analyze-header-filters"
import { VIEW_TYPES } from "helpers/base"
import { combineTanStackStatuses, DataStatus } from "helpers/data-statuses"
import { computeUpdatedFilters, computeAddedFilters } from "helpers/filters"
import { computeFilterOns, IntradayPricingFilterParams } from "helpers/filters/filter-ons"
import { useIsIntradayMode } from "connected-components/dashboard/hooks/use-intraday-state"
import { NORMALIZATION } from "helpers/segments"
import { computeEffectiveNormalization } from "helpers/filter-scope"
import { useAccountAlias } from "resources/account"
import { AssetFilterOptions, useAssetFilters, useSecuritiesDescriptors } from "resources/filters"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassificationsData } from "resources/classifications"
import { useAnalytics } from "connected-components/analytics"
import { Filter, FilterSecurity } from "connected-components/dashboard/context/types"
import useFilterSecuritiesData from "hooks/use-filter-securities-data"
import { DASHBOARD_EVENTS } from "../../analytics"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { selectCoreState } from "../../store/selectors/select-core-state"
import { DEFAULT_TRADE_STATE } from "../../store/trade-slice"
import { useCoreActions } from "../../hooks/use-core-actions"
import { useEffectiveFilterScope } from "../../hooks/use-effective-filter-scope"

export default function FilterSelector({
  setFilterSelectorStatus,
  hasMultipleInstruments,
  anchor,
  toggleFilters,
}: FilterSelectorProps) {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((state) => selectCoreState(state, dashboardId))
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const { trades, isExpanded } = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)
  const { instrument, secondInstrument, comparisonInstrument, endDate, base, filter, normalization, isIntraday, isEndVsStartDateComparison, filterScope } = coreState
  const { setFilter, setNormalization, setFilterScope } = useCoreActions()
  const effectiveFilterScope = useEffectiveFilterScope()
  const effectiveNormalization = computeEffectiveNormalization(normalization, effectiveFilterScope)
  const analytics = useAnalytics()
  const account = useAccountAlias()

  const isActive = !!base

  const isIntradayMode = useIsIntradayMode()
  const intradayPricingFilter = useMemo((): IntradayPricingFilterParams | undefined => {
    if (!isIntraday) {
      return undefined
    }
    return {
      isUnsupported: !isIntradayMode,
      tooltip: isIntradayMode ? undefined : "Only available when the end date is today",
    }
  }, [isIntraday, isIntradayMode])

  const modelIdStr = modelId ?? ""
  const [assetFilters, assetFiltersStatus] = useAssetFilters(modelIdStr, account, instrument, endDate, endDate, isIntraday)

  // This creates a TICKER.MIC id from each security in the filter
  const securitiesIds: string[] = useMemo(() => (Object.values(filter?.securities || {}) as FilterSecurity[][]).reduce((acc: string[], filterTypeSecurities: FilterSecurity[]): string[] =>
    [...acc, ...filterTypeSecurities.map(({ ticker, mic, country }) => `${ticker}.${mic || country}`)], []),
  [filter])

  const [filterSecurities, filterSecuritiesStatus] = useFilterSecuritiesData(
    modelIdStr,
    account,
    instrument,
    secondInstrument,
    comparisonInstrument,
    endDate,
    isExpanded,
  )

  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const [securitiesDescriptors, securitiesDescriptorsStatus] = useSecuritiesDescriptors(account, modelIdStr, securitiesIds, endDate)

  const {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: classificationsStatus,
  } = useClassificationsData(endDate, filter)

  const filterOns = useMemo(() => {
    if (isEmpty(assetFilters)) return []

    // [TODO:TS] Fix when typing the base.js file
    const viewType = (isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE) as "absolute" | "active"
    return computeFilterOns(
      viewType,
      assetFilters as AssetFilterOptions,
      filter,
      undefined,
      tagCategories,
      classifications?.map(({ versions, ...classification }) => ({ ...classification, version: versions[versions.length - 1] })) || [],
      classificationsValues,
      attributeCategories,
      instrument?.type,
      effectiveNormalization,
      hasMultipleInstruments,
      isEndVsStartDateComparison,
      intradayPricingFilter,
    )
  }, [isActive, assetFilters, filter, tagCategories, classifications, classificationsValues, attributeCategories, instrument?.type, effectiveNormalization, hasMultipleInstruments, isEndVsStartDateComparison, intradayPricingFilter])

  const handleUpdateFilters = useCallback((filters: unknown) => {
    // [TODO:TS] Type function
    const { filter }: { filter: Filter | null } = computeUpdatedFilters(filters)

    // Track filter changes
    if (filter && Object.keys(filter).length > 0) {
      analytics.track(DASHBOARD_EVENTS.FILTER_APPLIED, { filter })
    } else if (isEmpty(filter)) {
      analytics.track(DASHBOARD_EVENTS.FILTER_CLEARED)
    }

    setFilter(filter)
  }, [setFilter, analytics])

  const status = combineTanStackStatuses([
    classificationsStatus,
    filterSecuritiesStatus,
    tagCategoriesStatus,
    attributeCategoriesStatus,
    assetFiltersStatus,
    securitiesDescriptorsStatus,
  ])

  // This component's visual handle is actually the Filter button inside the Dashboard component. We expose the status to the
  // Dashboard component so it can disable the button while it's loading or if there's an error.
  useEffect(() => {
    setFilterSelectorStatus(status)
  }, [status, setFilterSelectorStatus])

  // [TODO:TS] Type function
  const addedFilters = useMemo(() => {
    return computeAddedFilters(filter, filterOns, {
      tagCategories,
      securitiesDescriptors,
      attributeCategories,
      classificationsInfos: {
        classificationsMetas: classifications,
        classificationsValues: flattenedClassificationsValues,
      },
      portfolioFilters: assetFilters,
    })
  }, [
    filter,
    filterOns,
    tagCategories,
    classifications,
    securitiesDescriptors,
    assetFilters,
    attributeCategories,
    flattenedClassificationsValues,
  ])

  const hasTrades = size(trades) > 0
  // [TODO] startDate, intraday, isExpanded, currency, hasUnsupportedContributionFilter
  return anchor && (
    <Filters
      account={account}
      assetId={instrument?.id}
      assetType={instrument?.type}
      endDate={endDate}
      filterOns={filterOns}
      heapAddFilterId="dashboard"
      modelId={modelId}
      securityIds={filterSecurities}
      normalization={normalization}
      forcedNormalization={hasTrades ? NORMALIZATION.CONTRIBUTION : undefined}
      setNormalization={setNormalization}
      disableNormalization={hasTrades}
      disableNormalizationTooltip={hasTrades ? "Standalone normalization is not supported for trades" : null}
      showFilterScope
      filterScope={filterScope}
      setFilterScope={setFilterScope}
      hasBenchmark={isActive}
      baseType={base?.type}
      filter={filter}
      isFilterScopeUnavailable={hasTrades}
      addedFilters={addedFilters}
      updateFilters={handleUpdateFilters}
      anchorElement={anchor}
      filtersVisible={!!anchor}
      toggleFilters={toggleFilters}
    />
  )
}

interface FilterSelectorProps {
  setFilterSelectorStatus: Dispatch<SetStateAction<DataStatus>>
  hasMultipleInstruments: boolean
  anchor: HTMLElement | null
  toggleFilters: () => void
}
